import React, { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";

const slides = [
  {
    eyebrow: "Pharma · India · Est. trust since 2010",
    title: ["Pioneering medical", "excellence — ", "trusted by 1000s of doctors."],
    italic: "trusted",
    body: "A new-generation Indian pharmaceutical company building evidence-based medicines across Psychiatry, Orthopedics, Neurology and General Medicine.",
    image:
      "https://images.unsplash.com/photo-1745420052756-1b1c294a6420?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBtZWRpY2FsJTIwbGFib3JhdG9yeSUyMHJlc2VhcmNoZXJ8ZW58MHx8fHwxNzc4NDcwMzk3fDA&ixlib=rb-4.1.0&q=85",
    badge: "Live",
    primaryCta: { label: "Explore products", to: "/products" },
    ghostCta: { label: "Talk to our team", href: "/#contact" },
    accent: "var(--mp-brand)",
  },
  {
    eyebrow: "Clinician-led · Evidence based",
    title: ["Trusted by", "1,200+ doctors", "across India."],
    italic: "1,200+ doctors",
    body: "From district hospitals to multi-speciality clinics — Marpay molecules are part of daily prescriptions because they're built with the clinician in mind.",
    image:
      "https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
    badge: "Doctor Trust",
    primaryCta: { label: "Read our story", to: "/about" },
    ghostCta: { label: "View products", to: "/products" },
    accent: "var(--mp-magenta)",
  },
  {
    eyebrow: "Four specialties · One standard",
    title: ["Psychiatry. Ortho.", "Neuro. General Medicine.", "Engineered with rigour."],
    italic: "rigour",
    body: "We don't try to make everything for everyone. We make the right medicines for the specialties our team understands deepest.",
    image:
      "https://images.unsplash.com/photo-1766297248027-864589dbd336?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtZWRpY2FsJTIwbGFib3JhdG9yeSUyMHJlc2VhcmNoZXJ8ZW58MHx8fHwxNzc4NDcwMzk3fDA&ixlib=rb-4.1.0&q=85",
    badge: "Therapeutic Focus",
    primaryCta: { label: "Browse therapeutic areas", to: "/products" },
    ghostCta: { label: "About Marpay", to: "/about" },
    accent: "var(--mp-gold)",
  },
  {
    eyebrow: "Founders · 15+ years industry experience",
    title: ["Built by veterans.", "Designed for", "rapid innovation."],
    italic: "rapid",
    body: "A team that spent careers inside India's largest pharma houses — leading, building distribution, and listening to doctors face-to-face. Now channelled into one focused mission.",
    image:
      "https://images.unsplash.com/photo-1758691461516-7e716e0ca135?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxkb2N0b3IlMjBwb3J0cmFpdCUyMHByb2Zlc3Npb25hbCUyMGhvc3BpdGFsfGVufDB8fHx8MTc3ODQ3MDM5N3ww&ixlib=rb-4.1.0&q=85",
    badge: "Founders' Note",
    primaryCta: { label: "Meet the team", to: "/about" },
    ghostCta: { label: "Partner with us", href: "/#contact" },
    accent: "var(--mp-brand)",
  },
];

export default function HeroCarousel() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start", duration: 30 });
  const [selected, setSelected] = useState(0);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    const id = setInterval(() => {
      if (emblaApi) emblaApi.scrollNext();
    }, 6500);
    return () => {
      clearInterval(id);
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  return (
    <section
      id="top"
      data-testid="hero-carousel"
      className="relative pt-28 md:pt-32 pb-10 md:pb-14 crosshair-bg"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {slides.map((s, i) => (
              <SlideCard slide={s} key={i} index={i} active={selected === i} />
            ))}
          </div>
        </div>
        {/* Controls */}
        <div className="mt-8 flex items-center justify-between gap-6">
          <div className="flex items-center gap-2" data-testid="carousel-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => scrollTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`carousel-dot-${i}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${selected === i
                  ? "w-10 bg-[var(--mp-brand)]"
                  : "w-5 bg-[var(--mp-border)] hover:bg-[var(--mp-text-2)]"
                  }`}
              />
            ))}
            <span className="ml-4 font-mono-display text-[11px] tracking-[0.25em] uppercase text-[var(--mp-text-2)]">
              0{selected + 1} / 0{slides.length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={scrollPrev}
              data-testid="carousel-prev"
              aria-label="Previous slide"
              className="h-11 w-11 rounded-full border border-[var(--mp-border)] flex items-center justify-center hover:border-[var(--mp-text)] hover:bg-white transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={scrollNext}
              data-testid="carousel-next"
              aria-label="Next slide"
              className="h-11 w-11 rounded-full border border-[var(--mp-border)] flex items-center justify-center hover:border-[var(--mp-text)] hover:bg-white transition-colors"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function SlideCard({ slide, index, active }) {
  const [t1, t2, t3] = slide.title;
  return (
    <div className="flex-[0_0_100%] min-w-0 px-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <div className="overline mb-5" data-testid={`slide-${index}-overline`}>
            <span className="inline-flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: slide.accent }} />
              {slide.eyebrow}
            </span>
          </div>
          <h1
            data-testid={`slide-${index}-title`}
            className={`font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-medium leading-[0.98] text-[var(--mp-text)] transition-all duration-700 ${active ? "opacity-100 translate-y-0" : "opacity-70 translate-y-1"
              }`}
          >
            {t1}
            <br />
            {t2.includes(slide.italic) ? (
              t2.split(slide.italic).map((part, idx, arr) => (
                <React.Fragment key={idx}>
                  {part}
                  {idx < arr.length - 1 && (
                    <span className="italic font-normal" style={{ color: slide.accent }}>
                      {slide.italic}
                    </span>
                  )}
                </React.Fragment>
              ))
            ) : (
              <>{t2}</>
            )}
            <br />
            {t3.includes(slide.italic) ? (
              t3.split(slide.italic).map((part, idx, arr) => (
                <React.Fragment key={idx}>
                  {part}
                  {idx < arr.length - 1 && (
                    <span className="italic font-normal" style={{ color: slide.accent }}>
                      {slide.italic}
                    </span>
                  )}
                </React.Fragment>
              ))
            ) : (
              <>{t3}</>
            )}
          </h1>
          <p className="mt-7 text-lg md:text-xl text-[var(--mp-text-2)] leading-relaxed max-w-2xl">
            {slide.body}
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            {slide.primaryCta.to ? (
              <Link
                to={slide.primaryCta.to}
                data-testid={`slide-${index}-primary`}
                className="btn-primary text-sm"
              >
                {slide.primaryCta.label} <ArrowUpRight className="h-4 w-4" />
              </Link>
            ) : (
              <a
                href={slide.primaryCta.href}
                data-testid={`slide-${index}-primary`}
                className="btn-primary text-sm"
              >
                {slide.primaryCta.label} <ArrowUpRight className="h-4 w-4" />
              </a>
            )}
            {slide.ghostCta.to ? (
              <Link to={slide.ghostCta.to} data-testid={`slide-${index}-ghost`} className="btn-ghost text-sm">
                {slide.ghostCta.label}
              </Link>
            ) : (
              <a href={slide.ghostCta.href} data-testid={`slide-${index}-ghost`} className="btn-ghost text-sm">
                {slide.ghostCta.label}
              </a>
            )}
          </div>
        </div>

        <div className="lg:col-span-5 order-1 lg:order-2 relative">
          <div className="relative rounded-3xl overflow-hidden border border-[var(--mp-border)] aspect-[4/5]">
            <img
              src={slide.image}
              alt={slide.title.join(" ")}
              className="w-full h-full object-cover"
              loading={index === 0 ? "eager" : "lazy"}
            />
            <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-[#0b1936]/85 via-[#0b1936]/40 to-transparent">
              <div
                className="font-mono-display text-[10px] tracking-[0.25em] uppercase mb-2"
                style={{ color: slide.accent }}
              >
                Lab 0{index + 1}
              </div>
              <div className="text-white font-display text-xl leading-tight">{slide.title[0]}</div>
            </div>
            <div className="absolute top-5 left-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-[11px] font-mono-display tracking-widest uppercase text-[var(--mp-text)]">
              <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: slide.accent }} />
              {slide.badge}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
